import type { IChatInfo } from "@/chats/chats-models";
import {
  removeDuplicates,
  removeInArrayBy,
  replaceInArrayBy,
  sortByCreatedAt,
} from "@/utils/array";
import { defined, isArray } from "@/utils/variable-evaluation";
import { createReducer } from "@reduxjs/toolkit";
import { isLockedTextMessage } from "./main/messenger-main-helpers";
import { IMessengerActions } from "./messenger-actions";
import { nextMessagesWithErrorMessage } from "./messenger-helpers";
import type {
  ICallInfo,
  ICallTipNotice,
  IDestructibleMessage,
  IMessageInfo,
} from "./messenger-models";
import {
  CHAT_ACCOUNT_NO_LONGER_EXISTS_ERROR_MESSAGE,
  CHAT_BLOCKED_COUNTRY_ERROR_MESSAGE,
  CHAT_BLOCKED_ERROR_MESSAGE,
  CHAT_CLOSED_ERROR_MESSAGE,
  CHAT_FROZEN_ERROR_MESSAGE_MODEL,
  CHAT_FROZEN_ERROR_MESSAGE_USER,
  ECallStatus,
  EMessageType,
  NO_CREDITS_ERROR_MESSAGE,
} from "./messenger-models";

export interface IMessengerModel {
  chat: IChatInfo | null;
  messages: IMessageInfo[] | null;
  nextMessagesUrl: string | null;
  isNotesOpen: boolean;
  noteText: string;
  editingNoteId: string | null;
  // blockedChats: string[];
  // frozenChats: string[];
  // noCreditsChats: string[];
  isForceBlocked: boolean;
  isForceFrozen: boolean;
  isForceNoCredits: boolean;
  chatGallery: IMessageInfo[];
  nextChatGalleryUrl: string;
  activeCall: ICallInfo | null;
  isCallMinimized: boolean;
  callElapsedSeconds: number | null;
  endedCall: ICallInfo | null;
  dismissedEndedCallId: string | null;
  callTip: ICallTipNotice | null;
}

const INITIAL_STATE: IMessengerModel = {
  chat: null,
  messages: null,
  nextMessagesUrl: null,
  isNotesOpen: false,
  noteText: "",
  editingNoteId: null,
  // blockedChats: [],
  // frozenChats: [],
  // noCreditsChats: [],
  isForceBlocked: false,
  isForceFrozen: false,
  isForceNoCredits: false,
  chatGallery: [],
  nextChatGalleryUrl: "",
  activeCall: null,
  isCallMinimized: false,
  callElapsedSeconds: null,
  endedCall: null,
  dismissedEndedCallId: null,
  callTip: null,
};

// A call that goes live closes the previous call's summary, which would
// otherwise stay on top of the new call screen and hide its controls.
function withoutEndedCallSummary(
  state: IMessengerModel,
  status: ECallStatus | undefined
): Pick<IMessengerModel, "endedCall" | "dismissedEndedCallId"> {
  const isLive =
    status === ECallStatus.Answered || status === ECallStatus.Active;
  if (!isLive || !state.endedCall) {
    return {
      endedCall: state.endedCall,
      dismissedEndedCallId: state.dismissedEndedCallId,
    };
  }
  return {
    endedCall: null,
    dismissedEndedCallId: state.endedCall.call_id || state.endedCall._id,
  };
}

function isLiveCall(call: ICallInfo) {
  return (
    call.status === ECallStatus.Created ||
    call.status === ECallStatus.Answered ||
    call.status === ECallStatus.Active
  );
}

// The server's billing clock for a call that is running, stamped with when it
// arrived. A live event always carries it (the server sends one when billing
// starts); a call loaded from the API only does once time has passed, because
// zero elapsed also means billing has not started.
function withServerClock(call: ICallInfo, isLiveEvent: boolean): ICallInfo {
  const elapsed = call.elapsed_seconds;
  const isBilling =
    call.status === ECallStatus.Active &&
    elapsed != null &&
    (isLiveEvent || elapsed > 0);
  if (!isBilling) {
    return call;
  }
  return {
    ...call,
    server_clock: { elapsed_seconds: elapsed, received_at: Date.now() },
  };
}

function isSameCall(call: ICallInfo | null, callId: string) {
  return !!call && (call._id === callId || call.call_id === callId);
}

function updateMessageCollections(
  state: IMessengerModel,
  updatedMessage: IMessageInfo
) {
  const updateOne = (message: IMessageInfo) => {
    if (message._id === updatedMessage._id) {
      return { ...updatedMessage };
    }
    if (message.parent_message?._id === updatedMessage._id) {
      return {
        ...message,
        parent_message: { ...updatedMessage },
      };
    }
    return message;
  };

  return {
    messages: (state.messages || []).map(updateOne),
    chatGallery: (state.chatGallery || []).map(updateOne),
  };
}

const clientSideMessages: IMessageInfo[] = [];

/** Reducer */
export const messengerReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(IMessengerActions.prepareGetChat, (state, action) => {
      return {
        ...state,
        chat: action.payload.cachedChat || null,
        messages: null,
      };
    })
    .addCase(
      IMessengerActions.successGetChat,
      (state, action): IMessengerModel => {
        const chat: IChatInfo = action.payload;

        const activeOtherUserId = window.location.pathname.split("/")[2];
        if (chat.other_user._id !== activeOtherUserId) {
          // don't do anything if active chat is already changed
          return state;
        }

        // reconsider local blocks
        // const isBlockedChat = state.blockedChats.indexOf(chat._id) !== -1;
        // const isFrozenChat = state.frozenChats.indexOf(chat._id) !== -1;
        // const isNoCreditsChat = state.noCreditsChats.indexOf(chat._id) !== -1;

        return {
          ...state,
          chat,
          // blockedChats: isBlockedChat ? state.blockedChats.filter((chatId) => chatId !== chat._id) : state.blockedChats,
          isForceBlocked: false,
          // frozenChats: isFrozenChat ? state.frozenChats.filter((chatId) => chatId !== chat._id) : state.frozenChats,
          isForceFrozen: false,
          // noCreditsChats: isNoCreditsChat
          //   ? state.noCreditsChats.filter((chatId) => chatId !== chat._id)
          //   : state.noCreditsChats,
          isForceNoCredits: false,
        };
      }
    )
    .addCase(
      IMessengerActions.resetGetChat,
      (state, action): IMessengerModel => {
        return {
          ...state,
          chat: null,
          messages: null,
        };
      }
    )
    .addCase(
      IMessengerActions.successGetChatReload,
      (state, action): IMessengerModel => {
        const chat: IChatInfo = action.payload;

        // reconsider local blocks
        // const isBlockedChat = state.blockedChats.indexOf(chat._id) !== -1;
        // const isFrozenChat = state.frozenChats.indexOf(chat._id) !== -1;
        // const isNoCreditsChat = state.noCreditsChats.indexOf(chat._id) !== -1;

        return {
          ...state,
          chat,
          // blockedChats: isBlockedChat ? state.blockedChats.filter((chatId) => chatId !== chat._id) : state.blockedChats,
          // isForceBlocked: false,
          // frozenChats: isFrozenChat ? state.frozenChats.filter((chatId) => chatId !== chat._id) : state.frozenChats,
          // isForceFrozen: false,
          // noCreditsChats: isNoCreditsChat
          //   ? state.noCreditsChats.filter((chatId) => chatId !== chat._id)
          //   : state.noCreditsChats,
          // isForceNoCredits: false,
        };
      }
    )
    .addCase(
      IMessengerActions.successPostCreateChat,
      (state, action): IMessengerModel => {
        return {
          ...state,
          chat: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.updateChatById,
      (state, action): IMessengerModel => {
        const { chat_id, newValues } = action.payload;

        if (!state.chat || chat_id !== state.chat._id) {
          return state;
        }

        return {
          ...state,
          chat: { ...state.chat, ...newValues },
        };
      }
    )
    .addCase(
      IMessengerActions.successGetMessages,
      (state, action): IMessengerModel => {
        // const chatId = action.payload.chatId as string;
        const otherUserId = action.payload.otherUserId as string;
        const res = action.payload.res;

        const activeOtherUserId = window.location.pathname.split("/")[2];
        if (otherUserId !== activeOtherUserId) {
          // don't do anything if active chat is already changed
          return state;
        }

        const newMessages = res.data;

        let messages = newMessages || [];
        const currentChatClientSideMessages = clientSideMessages.filter(
          (m) => state.chat && m.chat_id === state.chat._id
        );

        // add client side messages if any
        messages = [...messages, ...currentChatClientSideMessages];
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          nextMessagesUrl: res.data.length > 0 ? res.next || "" : "",
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.resetGetMessages,
      (state, action): IMessengerModel => {
        return {
          ...state,
          messages: null,
        };
      }
    )
    .addCase(
      IMessengerActions.successGetNextMessages,
      (state, action): IMessengerModel => {
        const newMessages = action.payload.data;

        let messages = [...newMessages, ...(state.messages || [])];
        messages = removeDuplicates(messages, "_id", true);
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          nextMessagesUrl: action.payload.next || "",
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessagesRead,
      (state, action): IMessengerModel => {
        const config = action.payload.config;
        const { timestampString, messageIds, destructibles } = config;
        let messageList = state.messages;
        if (messageList) {
          messageList.forEach((message) => {
            messageIds.forEach((id: string) => {
              if (id === message._id) {
                messageList = replaceInArrayBy("_id", messageList!, id, {
                  ...message,
                  is_read: true,
                  read_at: timestampString,
                });
              }
            });
            if (
              defined(destructibles) &&
              isArray(destructibles) &&
              destructibles.length > 0
            ) {
              destructibles.forEach((destructible: IDestructibleMessage) => {
                if (destructible.message_id === message._id) {
                  messageList = replaceInArrayBy(
                    "_id",
                    messageList!,
                    destructible.message_id,
                    {
                      ...message,
                      destruct_at: destructible.destruct_at,
                    }
                  );
                }
              });
            }
          });
        }
        return { ...state, messages: messageList };
      }
    )
    .addCase(
      IMessengerActions.updateMessageRevealMedia,
      (state, action): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) =>
            message._id === action.payload.messageId
              ? {
                  ...message,
                  isRevealed: true,
                }
              : message
          );
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsPaid,
      (state, action): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.messageId) {
              return {
                ...message,
                is_paid: true,
                destruct_at: action.payload.destructAt,
              };
            }
            if (message.parent_message?._id === action.payload.messageId) {
              return {
                ...message,
                parent_message: {
                  ...(message.parent_message as IMessageInfo),
                  is_paid: true,
                  destruct_at: action.payload.destructAt,
                },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsHidden,
      (state, action): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.hiddenMessage._id) {
              return { ...action.payload.hiddenMessage };
            }
            if (
              message.parent_message?._id === action.payload.hiddenMessage._id
            ) {
              return {
                ...message,
                parent_message: { ...action.payload.hiddenMessage },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsDeleted,
      (state, action): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.deletedMessage._id) {
              return { ...action.payload.deletedMessage };
            }
            if (
              message.parent_message?._id === action.payload.deletedMessage._id
            ) {
              return {
                ...message,
                parent_message: { ...action.payload.deletedMessage },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessage,
      (state, action): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.message._id) {
              return { ...action.payload.message };
            }
            if (message.parent_message?._id === action.payload.message._id) {
              return {
                ...message,
                parent_message: { ...action.payload.message },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsExpired,
      (state, action): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message): IMessageInfo => {
            if (message._id === action.payload.messageId) {
              // Replace message with expired placeholder, preserving existing state and price info
              return {
                ...message,
                text: "This content has expired",
                type: EMessageType.Text,
                is_deleted: true,
                // Remove only media and self-destruct properties
                image: undefined,
                video: undefined,
                self_destruct: undefined,
                self_destruct_hours: undefined,
                destruct_at: undefined,
              } as IMessageInfo;
            }
            if (message.parent_message?._id === action.payload.messageId) {
              // Also replace in parent message if needed
              return {
                ...message,
                parent_message: {
                  ...message.parent_message,
                  text: "This content has expired",
                  type: EMessageType.Text,
                  is_deleted: true,
                  // Remove only media and self-destruct properties
                  image: undefined,
                  video: undefined,
                  self_destruct: undefined,
                  self_destruct_hours: undefined,
                  destruct_at: undefined,
                } as IMessageInfo,
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.removeClientSideMessage,
      (state, action): IMessengerModel => {
        const messages = removeInArrayBy(
          "__clientSideSendId",
          state.messages || [],
          action.payload.__clientSideSendId
        );

        // remove from global clientSideMessages too
        const index = clientSideMessages.findIndex(
          (m) => m.__clientSideSendId === action.payload.__clientSideSendId
        );
        if (index !== -1) {
          clientSideMessages.splice(index, 1);
        }
        // sessionStorage.removeItem(`client-side-message-${action.payload.__clientSideSendId}`);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.resetPostSendMessage,
      (state, action): IMessengerModel => {
        return state;
      }
    )
    .addCase(
      IMessengerActions.successGetMessageById,
      (state, action): IMessengerModel => {
        const updatedMessage: IMessageInfo = action.payload;
        const { messages, chatGallery } = updateMessageCollections(
          state,
          updatedMessage
        );

        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.successGetFromLastIdMessages,
      (state, action): IMessengerModel => {
        const { res, config } = action.payload;
        const messagesFromLastId = res.data;

        if (!state.chat || config.chat_id !== state.chat._id) {
          return state;
        }

        let messages = [
          ...(state.messages || []).filter(
            (message) => !defined(message.clientSideReceiveId)
          ),
          ...messagesFromLastId,
        ];
        messages = removeDuplicates(messages, "_id", true);
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.addClientSideMessageReceived,
      (state, action): IMessengerModel => {
        const clientSideMessage: IMessageInfo =
          action.payload.clientSideMessage;

        if (
          !state.chat ||
          clientSideMessage.sender_id !== state.chat.other_user._id
        ) {
          return state;
        }

        let messages = state.messages || [];

        messages = [...messages, clientSideMessage];
        messages = removeDuplicates(messages, "_id", true);
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.initPostSendMessage,
      (state, action): IMessengerModel => {
        const newMessage: IMessageInfo = {
          ...action.payload.clientSideMessage,
        }; // this can be both media or text
        delete newMessage.error; // remove error on init (resend)

        const index = clientSideMessages.findIndex(
          (m) => m.__clientSideSendId === newMessage.__clientSideSendId
        );
        if (index !== -1) {
          // resend?
          clientSideMessages.splice(index, 1, newMessage); // replace
        } else {
          clientSideMessages.push(newMessage);
        }
        // sessionStorage.setItem(`client-side-message-${newMessage.__clientSideSendId}`, JSON.stringify(newMessage));

        if (
          !state.chat ||
          newMessage.chat_id !== state.chat._id ||
          state.messages === null
        ) {
          return state;
        }

        let messages = state.messages || [];

        let isAlreadyInTheList = false; // for resend it will be already there
        messages = messages.map((message: IMessageInfo) => {
          if (message.__clientSideSendId === newMessage.__clientSideSendId) {
            isAlreadyInTheList = true;
            return newMessage;
          }
          return message;
        });
        if (!isAlreadyInTheList) {
          messages = [...messages, newMessage];
        }
        messages.sort(sortByCreatedAt);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.updateSendMessageProgress,
      (state, action): IMessengerModel => {
        // update in global clientSideMessages too with new progress value
        const oldMessage = clientSideMessages.find(
          (m) => m.__clientSideSendId === action.payload.__clientSideSendId
        );
        let newMessage: IMessageInfo | null = null;
        if (oldMessage !== null) {
          newMessage = {
            ...oldMessage!,
            uploadProgress: action.payload.progress,
            loaded: action.payload.loaded,
            total: action.payload.total,
          };
          const index = clientSideMessages.findIndex(
            (m) => m.__clientSideSendId === action.payload.__clientSideSendId
          );
          clientSideMessages.splice(index, 1, newMessage);
        }

        if (
          !state.chat ||
          (newMessage && newMessage.chat_id !== state.chat._id) ||
          state.messages === null
        ) {
          return state;
        }

        let messages = state.messages || [];

        let isAlreadyInTheList = false;
        messages = messages.map((message) => {
          if (
            newMessage &&
            message.__clientSideSendId === newMessage.__clientSideSendId
          ) {
            isAlreadyInTheList = true;
            return newMessage;
          }
          return message;
        });
        if (!isAlreadyInTheList) {
          // to handle cases like exit chat and return back
          // if this message is from this chat but is not in the list
          // we have to add it
          messages = [...messages!, newMessage!];
        }
        messages.sort(sortByCreatedAt);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.successPostSendMessage,
      (state, action): IMessengerModel => {
        // remove from global clientSideMessages too
        const index = clientSideMessages.findIndex(
          (m) => m.__clientSideSendId === action.payload.__clientSideSendId
        );
        if (index !== -1) {
          clientSideMessages.splice(index, 1);
        }
        // sessionStorage.removeItem(`client-side-message-${__clientSideSendId}`);

        const newMessage: IMessageInfo = action.payload.message;

        if (!state.chat || newMessage.chat_id !== state.chat._id) {
          return state;
        }

        let messages = state.messages || [];
        messages = replaceInArrayBy(
          "__clientSideSendId",
          messages,
          action.payload.__clientSideSendId,
          { ...newMessage, clientSideKey: action.payload.__clientSideSendId }
        );
        messages = removeDuplicates(messages, "_id");

        const chat = isLockedTextMessage(newMessage)
          ? {
              ...state.chat,
              locked_messages_total:
                (state.chat.locked_messages_total ?? 0) +
                (newMessage.unlock_price ?? 0),
            }
          : state.chat;

        return {
          ...state,
          chat,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.failurePostSendMessage,
      (state, action): IMessengerModel => {
        const newMessage: IMessageInfo = action.payload;

        // let blockedChats = state.blockedChats;
        let isForceBlocked = state.isForceBlocked;
        if (
          action.payload.error.message === CHAT_CLOSED_ERROR_MESSAGE ||
          action.payload.error.message === CHAT_BLOCKED_ERROR_MESSAGE ||
          action.payload.error.message === CHAT_BLOCKED_COUNTRY_ERROR_MESSAGE ||
          action.payload.error.message ===
            CHAT_ACCOUNT_NO_LONGER_EXISTS_ERROR_MESSAGE
        ) {
          // blockedChats = addToArrayIfNotThere(
          //   blockedChats,
          //   action.payload.chat_id
          // );
          isForceBlocked = true;
        }

        // let frozenChats = state.frozenChats;
        let isForceFrozen = state.isForceFrozen;
        if (
          action.payload.error.message === CHAT_FROZEN_ERROR_MESSAGE_MODEL ||
          action.payload.error.message === CHAT_FROZEN_ERROR_MESSAGE_USER
        ) {
          // frozenChats = addToArrayIfNotThere(frozenChats, action.payload.chat_id);
          isForceFrozen = true;
        }

        // let noCreditsChats = state.noCreditsChats;
        let isForceNoCredits = state.isForceNoCredits;
        if (action.payload.error.message === NO_CREDITS_ERROR_MESSAGE) {
          // noCreditsChats = addToArrayIfNotThere(
          //   noCreditsChats,
          //   action.payload.chat_id
          // );
          isForceNoCredits = true;
        }

        let messages = state.messages || [];
        if (
          state.chat &&
          state.chat.other_user._id === newMessage.recipient_id
        ) {
          messages = nextMessagesWithErrorMessage(
            messages,
            action.payload,
            "__clientSideSendId"
          );
        }

        return {
          ...state,
          // blockedChats,
          isForceBlocked,
          // frozenChats,
          isForceFrozen,
          // noCreditsChats,
          isForceNoCredits,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.successPatchBuyPaidContent,
      (state, action): IMessengerModel => {
        const updatedMessage: IMessageInfo = action.payload;
        const { messages, chatGallery } = updateMessageCollections(
          state,
          updatedMessage
        );
        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageUnlockByPayingUsd,
      (state, action): IMessengerModel => {
        const unlockedMessage = action.payload.message;
        const { messages, chatGallery } = updateMessageCollections(
          state,
          unlockedMessage
        );
        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.successPostChatNote,
      (state, action): IMessengerModel => {
        return {
          ...state,
          chat: state.chat
            ? {
                ...state.chat,
                notes: [...state.chat.notes, action.payload],
              }
            : state.chat,
        };
      }
    )
    .addCase(
      IMessengerActions.successPatchChatNote,
      (state, action): IMessengerModel => {
        return {
          ...state,
          chat: state.chat
            ? {
                ...state.chat,
                notes: state.chat.notes.map((note) =>
                  note._id === action.payload._id ? action.payload : note
                ),
              }
            : state.chat,
        };
      }
    )
    .addCase(
      IMessengerActions.successDeleteChatNote,
      (state, action): IMessengerModel => {
        return {
          ...state,
          chat: state.chat
            ? {
                ...state.chat,
                notes: state.chat.notes.filter(
                  (note) => note._id !== action.payload.id
                ),
              }
            : state.chat,
        };
      }
    )
    .addCase(
      IMessengerActions.setNoteText,
      (state, action): IMessengerModel => {
        return {
          ...state,
          noteText: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.setEditingNoteId,
      (state, action): IMessengerModel => {
        return {
          ...state,
          editingNoteId: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.setNotesOpen,
      (state, action): IMessengerModel => {
        return {
          ...state,
          isNotesOpen: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.successDeleteMessage,
      (state, action): IMessengerModel => {
        const deletedMessage = action.payload.response;
        const { messages, chatGallery } = updateMessageCollections(
          state,
          deletedMessage
        );
        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.successPatchHideMessage,
      (state, action): IMessengerModel => {
        const hiddenMessage = action.payload.response;
        const { messages, chatGallery } = updateMessageCollections(
          state,
          hiddenMessage
        );
        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.successPostMessageReaction,
      (state, action): IMessengerModel => {
        const { response } = action.payload;
        if (!response) {
          return state;
        }

        const { messages, chatGallery } = updateMessageCollections(
          state,
          response
        );

        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.successDeleteMessageReaction,
      (state, action): IMessengerModel => {
        const { response } = action.payload;
        if (!response) {
          return state;
        }

        const { messages, chatGallery } = updateMessageCollections(
          state,
          response
        );

        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.successGetChatGallery,
      (state, action): IMessengerModel => {
        return {
          ...state,
          chatGallery: action.payload.data || [],
          nextChatGalleryUrl: action.payload.next || "",
        };
      }
    )
    .addCase(
      IMessengerActions.successGetChatGalleryNext,
      (state, action): IMessengerModel => {
        return {
          ...state,
          chatGallery: [...state.chatGallery, ...(action.payload.data || [])],
          nextChatGalleryUrl: action.payload.next || "",
        };
      }
    )
    .addCase(
      IMessengerActions.removeExpiredFromChatGallery,
      (state, action): IMessengerModel => {
        const chatGallery = state.chatGallery.filter(
          (message) => message._id !== action.payload.messageId
        );
        return {
          ...state,
          chatGallery,
        };
      }
    )
    .addCase(IMessengerActions.setActiveCall, (state, action) => {
      const current = state.activeCall;
      const incoming = action.payload.call;
      const currentIsLive =
        current?.status === ECallStatus.Answered ||
        current?.status === ECallStatus.Active;
      if (
        currentIsLive &&
        !isSameCall(current, incoming.call_id || incoming._id)
      ) {
        return state;
      }
      return {
        ...state,
        ...withoutEndedCallSummary(state, incoming.status),
        activeCall: withServerClock(
          { ...incoming, is_incoming: action.payload.is_incoming },
          false
        ),
      };
    })
    .addCase(IMessengerActions.mergeActiveCall, (state, action) => {
      const incomingId = action.payload.call_id || action.payload._id;
      if (!incomingId || !isSameCall(state.activeCall, incomingId)) {
        return state;
      }
      return {
        ...state,
        ...withoutEndedCallSummary(state, action.payload.status),
        activeCall: withServerClock(
          { ...state.activeCall!, ...action.payload },
          true
        ),
      };
    })
    .addCase(IMessengerActions.reconcileLiveCall, (state, action) => {
      const { askedAboutCallId, liveCall } = action.payload;
      const current = state.activeCall;
      if (!current || !isLiveCall(current)) {
        return state;
      }
      const liveCallId = liveCall?.call_id || liveCall?._id;
      if (liveCall && liveCallId && isSameCall(current, liveCallId)) {
        // Still the same call, possibly further along than this app knows
        // (answered while the events were missed).
        return {
          ...state,
          ...withoutEndedCallSummary(state, liveCall.status),
          activeCall: withServerClock({ ...current, ...liveCall }, false),
        };
      }
      if (!askedAboutCallId || !isSameCall(current, askedAboutCallId)) {
        return state;
      }
      // It was on screen when the API was asked and the API does not list it:
      // it ended while the events were missed.
      return {
        ...state,
        activeCall: null,
        isCallMinimized: false,
        callElapsedSeconds: null,
      };
    })
    .addCase(IMessengerActions.clearActiveCall, (state, action) => {
      if (isSameCall(state.activeCall, action.payload.callId)) {
        return {
          ...state,
          activeCall: null,
          isCallMinimized: false,
          callElapsedSeconds: null,
        };
      }
      return state;
    })
    .addCase(IMessengerActions.setCallMinimized, (state, action) => {
      if (state.isCallMinimized === action.payload) {
        return state;
      }
      return { ...state, isCallMinimized: action.payload };
    })
    .addCase(IMessengerActions.setCallElapsedSeconds, (state, action) => {
      if (state.callElapsedSeconds === action.payload) {
        return state;
      }
      return { ...state, callElapsedSeconds: action.payload };
    })
    .addCase(IMessengerActions.showCallTip, (state, action) => {
      if (state.callTip?.id === action.payload.id) {
        return state;
      }
      return { ...state, callTip: action.payload };
    })
    .addCase(IMessengerActions.addCallTipNote, (state, action) => {
      const { id, chat_id, note } = action.payload;
      const tip = state.callTip;
      const isThatTip =
        !!tip &&
        ((!!id && tip.id === id) || (!!chat_id && tip.chat_id === chat_id));
      if (!tip || !isThatTip || tip.note) {
        return state;
      }
      return { ...state, callTip: { ...tip, note } };
    })
    .addCase(IMessengerActions.clearCallTip, (state, action) => {
      if (state.callTip?.id !== action.payload.id) {
        return state;
      }
      return { ...state, callTip: null };
    })
    .addCase(IMessengerActions.setEndedCall, (state, action) => {
      // Both the End response and the call_ended socket event land here; a late
      // duplicate must not reopen a summary the user already closed.
      if (isSameCall(action.payload, state.dismissedEndedCallId ?? "")) {
        return state;
      }
      return { ...state, endedCall: action.payload };
    })
    .addCase(IMessengerActions.clearEndedCall, (state) => {
      return {
        ...state,
        endedCall: null,
        dismissedEndedCallId: state.endedCall
          ? state.endedCall.call_id || state.endedCall._id
          : state.dismissedEndedCallId,
      };
    });
});
